import { A11yModule } from '@angular/cdk/a11y';
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  forwardRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { SnackbarService } from '../../snackbar';
import { PreviewFileDialogComponent } from './preview-file-dialog.component';

/**
 * Defines the structure for the uploaded file object.
 */
export interface UploadedFile {
  file: string | ArrayBuffer | null; // Base64 encoded file content
  fileId: string | null;
  nomeFile: string;
  type: string;
  fromMoniser?: boolean;
}

@Component({
  imports: [
    NgTemplateOutlet,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    TranslatePipe,
    MatDialogModule,
    A11yModule,
    MatTooltip,
  ],
  selector: 'siat-upload-file',
  templateUrl: './upload-file.component.html',
  styleUrl: './upload-file.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UploadFileComponent),
      multi: true,
    },
  ],
})
export class UploadFileComponent implements ControlValueAccessor {
  /**
   * A boolean field is readonly.
   */
  readonly = input<boolean>(false);

  /**
   * Disabled state set by Angular Forms via setDisabledState(), e.g. when the
   * bound FormControl is disabled for a read-only collaboratore. Unlike
   * `readonly`, this keeps the normal upload UI visible, just non-interactive.
   */
  formDisabled = signal(false);

  /**
   * If readonly, this is the value. Accepts both a single file and an array
   * for backward compatibility with records saved before multi-upload.
   */
  value = input<UploadedFile | UploadedFile[]>();

  /**
   * The field label.
   */
  label = input<string>('Carica file');

  /**
   * The field placeholder.
   */
  placeholder = input<string>('');

  /**
   * The field id.
   */
  id = input<string>('');

  /**
   * The field name.
   */
  name = input<string>('');

  /**
   * The file types to accept.
   */
  accept = input<string>('application/pdf,image/jpeg,image/png');

  /**
   * Maximum allowed file size in bytes. Defaults to 10 MB.
   */
  maxSize = input<number>(10 * 1024 * 1024);

  /**
   * If true (default), allows uploading multiple files.
   * If false, only one file can be uploaded at a time.
   */
  multiple = input<boolean>(true);

  /**
   * Where the list of selected files is shown, relative to the drop zone.
   */
  listPosition = input<'above' | 'below'>('below');

  /**
   * A reference to the hidden file input element.
   * Optional because the input is inside @if (!readonly()) and won't exist in readonly mode.
   */
  fileInputRef = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  /**
   * Whether a file is being dragged over the upload area.
   */
  dragActive = false;

  /**
   * The list of currently selected files, used as the source of truth for display and form value.
   */
  controlValue: UploadedFile[] = [];

  /**
   * Emits when the user requests deletion of a file that has already been saved (has a fileId).
   */
  onDelete = output<UploadedFile>();

  /**
   * Mat dialog service.
   */
  private dialog = inject(MatDialog);

  /**
   * Snackbar service.
   */
  private snackBarService = inject(SnackbarService);

  /**
   * Change detector, used to refresh the view after the asynchronous file read.
   */
  private cdr = inject(ChangeDetectorRef);

  /**
   * Resolver for the preview dialog. Called with a fileId to fetch the file content URL.
   */
  previewResolver =
    input<(fileId: string) => Observable<string | ArrayBuffer | null>>();

  constructor() {
    effect(() => {
      if (this.readonly()) {
        const val = this.value();
        if (!val) {
          this.controlValue = [];
        } else {
          this.controlValue = Array.isArray(val) ? val : [val];
        }
      }
    });
  }

  /**
   * Handles the file selection from the input element.
   * It reads the file, converts it to base64,
   * and populates the form control with the structured object.
   */
  onFileSelected(event: Event): void {
    if (this.formDisabled()) return;
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    if (this.multiple()) {
      Array.from(input.files).forEach(file => this.handleFile(file));
    } else {
      this.handleFile(input.files[0]);
    }
  }

  /**
   * Handles the dragover event on the upload area.
   */
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.formDisabled()) return;
    if (this.multiple() || this.controlValue.length === 0) {
      this.dragActive = true;
    }
  }

  /**
   * Handles the dragleave event on the upload area.
   */
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragActive = false;
  }

  /**
   * Handles the drop event on the upload area.
   */
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragActive = false;
    if (this.formDisabled()) return;

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;

    if (this.multiple()) {
      Array.from(files).forEach(file => this.handleFile(file));
    } else if (this.controlValue.length === 0) {
      this.handleFile(files[0]);
    }
  }

  private handleFile(file: File): void {
    if (file.size > this.maxSize()) {
      const maxMb = this.maxSize() / (1024 * 1024);
      this.snackBarService.showDangerSnackbar(
        `Il file supera la dimensione massima consentita di ${maxMb} MB`
      );
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const fileValue: UploadedFile = {
        file: reader.result,
        fileId: null,
        nomeFile: file.name,
        type: file.type,
      };

      if (this.multiple()) {
        this.controlValue = [...this.controlValue, fileValue];
      } else {
        this.controlValue = [fileValue];
      }

      this.emitChange();
      this.cdr.markForCheck();
    };
    reader.onerror = () => {
      this.snackBarService.showDangerSnackbar(
        'Errore durante il caricamento del file'
      );
    };
    reader.readAsDataURL(file);
  }

  /**
   * Removes a file by index.
   * If the file has a fileId (already saved on the server), emits onDelete for the parent to handle.
   * If the file is only local (no fileId), removes it directly from the list.
   */
  removeFile(index: number): void {
    if (this.formDisabled()) return;
    const fileValue = this.controlValue[index];
    if (!fileValue) return;

    if (fileValue.fileId) {
      this.onDelete.emit(fileValue);
    } else {
      this.controlValue = this.controlValue.filter((_, i) => i !== index);
      this.resetFileInput();
      this.emitChange();
    }
  }

  /**
   * Gestisce la logica per aprire l'anteprima del file specificato.
   */
  previewFile(index: number): void {
    const fileData = this.controlValue[index];
    if (!fileData) return;

    if (fileData.file) {
      this.openPreviewModal(fileData.file as string, fileData.type);
    } else if (fileData.fileId) {
      const resolver = this.previewResolver();
      if (!resolver) return;

      resolver(fileData.fileId).subscribe({
        next: contentUrl => {
          if (contentUrl) {
            this.openPreviewModal(contentUrl as string, fileData.type);
          }
        },
        error: () => {
          this.snackBarService.showDangerSnackbar(
            "Errore durante il recupero dell'anteprima"
          );
        },
      });
    }
  }

  /**
   * Returns the material icon name based on the file MIME type.
   */
  fileIconName(index: number): string {
    const type = this.controlValue[index]?.type ?? '';
    return type.startsWith('image/') ? 'image' : 'description';
  }

  /**
   * @override
   */
  writeValue(value: UploadedFile | UploadedFile[] | null): void {
    console.warn('[UploadFile writeValue] received:', value);
    if (!value) {
      this.controlValue = [];
      this.resetFileInput();
      return;
    }
    this.controlValue = Array.isArray(value) ? value : [value];
    console.warn(
      '[UploadFile writeValue] controlValue after set:',
      this.controlValue
    );
  }

  /**
   * @override
   */
  onChange: (value: UploadedFile[] | null) => void = () => {};

  /**
   * @override
   */
  onTouched: () => void = () => {};

  /**
   * @override
   */
  registerOnChange(fn: (value: UploadedFile[] | null) => void): void {
    this.onChange = fn;
  }

  /**
   * @override
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * @override
   */
  setDisabledState(isDisabled: boolean): void {
    this.formDisabled.set(isDisabled);
  }

  /**
   * Apre la modale di anteprima.
   */
  private openPreviewModal(contentUrl: string, type: string): void {
    this.dialog.open(PreviewFileDialogComponent, {
      width: '80vw',
      data: { contentUrl, type },
    });
  }

  /**
   * Resets the native file input element so the same file can be re-selected after removal.
   */
  private resetFileInput(): void {
    const ref = this.fileInputRef();
    if (ref) {
      ref.nativeElement.value = '';
    }
  }

  private emitChange(): void {
    this.onChange(this.controlValue.length > 0 ? this.controlValue : null);
    this.onTouched();
  }
}
